<template>
	<div class="fill-height d-flex flex-column flex-grow-1">
		<v-card class="skode pa-1 pa-sm-4 pt-sm-8 fill-height my-8" :elevation="$vuetify.breakpoint.smAndUp > 0 ? 1 : 0">
			<div class="header-grid mt-4 mt-sm-0">
				<div class="d-flex align-center">
					<h3>Škode</h3>
				</div>
				<div class="d-flex align-center sm:justify-center fluid">
					<div>Polja:</div>
					<v-select
						v-model="visibleFields"
						:items="fieldsOptions"
						dense
						single-line
						multiple
						class="ml-2 mb-1 sm:width-fields"
						hide-details
						max-height="800px"
					>
						<template v-slot:selection="{ item, index }">
							<v-chip v-if="index === 0">
								<span>{{ item.text }}</span>
							</v-chip>
							<span v-if="index === 1" class="grey--text caption">(+{{ visibleFields.length - 1 }} drugih)</span>
						</template>
					</v-select>
				</div>

				<div class="d-flex align-center sm:justify-center">
					<div>Grupiranje:</div>
					<v-select v-model="groupBy" :items="groupByOptions" dense single-line class="ml-2 mb-1 sm:width-group-by" inverted hide-details></v-select>
				</div>

				<div class="d-flex align-center sm:justify-center">
					<div>od &nbsp;</div>
					<v-select v-model="fromYear" :items="fromYearOptions" dense single-line class="ml-2 mb-1 width-from-year" inverted hide-details></v-select>
					<div>&nbsp;do&nbsp;</div>
					<v-select v-model="toYear" :items="toYearOptions" dense single-line class="ml-2 mb-1 width-from-year" inverted hide-details></v-select>
				</div>
				<div class="d-flex align-center justify-center">
					<v-btn-toggle tile v-model="toggleStatus" dense multiple class="mx-0 mx-sm-5" color="primary darken-2 d-flex align-center">
						<v-btn small :disabled="loading" value="P">Prejete</v-btn>

						<v-btn small :disabled="loading" value="O">Odobrene</v-btn>

						<v-btn small :disabled="loading" value="Z">Zavrnjene</v-btn>
					</v-btn-toggle>
				</div>
				<div class="d-flex align-center justify-center">
					<v-text-field
						prepend-icon="mdi-magnify"
						label="Iskanje"
						class="mb-1 search"
						hide-details
						clearable
						dense
						single-line
						ref="searchInput"
						@blur="setSearchValue"
						@keyup.enter="setSearchValue"
						:value="search"
					></v-text-field>
				</div>
			</div>

			<v-btn @click="reload" color="yellow" fab top right absolute title="Obnovi podatke" class="mr-36">
				<v-icon large color="white">mdi-reload</v-icon>
			</v-btn>

			<v-btn @click="showSaveModal" color="green" fab top right absolute title="Shrani poizvedbo" class="mr-24">
				<v-icon large color="white">mdi-content-save</v-icon>
			</v-btn>

			<v-btn @click="exportInternalDataToXLS" color="blue" fab top right absolute title="Izvoz v XLS" class="mr-12">
				<v-icon large color="white">mdi-file-excel</v-icon>
			</v-btn>

			<v-btn to="/skode/nova" color="red" fab top right absolute>
				<v-icon large color="white">mdi-plus</v-icon>
			</v-btn>

			<v-data-table
				ref="table"
				dense
				mobile-breakpoint="1300px"
				:headers="headers"
				:items="filteredRecords"
				:sort-by="[]"
				:sort-desc="[]"
				:items-per-page.sync="itemsPerPage"
				:search="search"
				:custom-filter="filterByManyFields"
				multi-sort
				class="elevation-0"
				locale="sl"
				:loading="loading"
				hide-default-footer
				:group-by="realGroupBy"
				:group-desc="realGroupSortDesc"
				@click:row="handleClick"
			>
				<template v-slot:top="{ pagination, options, updateOptions }">
					<v-data-footer
						:pagination="pagination"
						:options="options"
						@update:options="updateOptions"
						:items-per-page-options="itemsPerPageOptions"
						items-per-page-text="$vuetify.dataTable.itemsPerPageText"
					/>
				</template>

				<template v-slot:item.Datum_SQL="{ item }">
					<div v-html="humanDatum(item.Datum_SQL)"></div>
				</template>
				<template v-slot:item.Aktualni_km="{ item }">
					<span v-if="item.Stanje_km > item.Aktualni_km">
						<b style="color: red" class="font-xs">MANJKA KM</b>
					</span>
					<span v-else>
						{{ km(item.Aktualni_km) }}
					</span>
				</template>
				<template v-slot:item.Znesek_izplacilo="{ item }">
					<div :class="`status-${item.Status}`">
						{{ eur(item.Znesek_izplacilo) }}
					</div>
				</template>
				<template v-slot:item.Skupaj_s_popustom="{ item }">
					<div>
						{{ eur(item.Skupaj_s_popustom) }}
					</div>
				</template>
				<template v-slot:item.Znesek_zahtevek="{ item }">
					<div>{{ eur(item.Znesek_zahtevek) }}</div>
				</template>
				<template v-slot:item.Znesek_soudelezba="{ item }">
					<div>{{ eur(item.Znesek_soudelezba) }}</div>
				</template>
				<template v-slot:group.header="{ group, headers, items }">
					<td :colspan="headers.length">
						<span class="font-weight-medium pl-2">
							{{ group }} - {{ items.length }} zapisov, izplačilo
							{{ eur(items.reduce((t, i) => t + parseFloat(i.Znesek_izplacilo), 0)) }}
						</span>
					</td>
				</template>
			</v-data-table>
		</v-card>
		<v-dialog v-model="dialog" persistent max-width="600px">
			<v-card>
				<v-card-title>
					<span class="headline">Shrani poizvedbo</span>
				</v-card-title>
				<v-card-text>
					<v-container>
						<v-row>
							<v-col cols="12">
								<v-text-field ref="formName" label="Ime" name="name" required placeholder="npr. Prejete škode letos"></v-text-field>
							</v-col>
						</v-row>
						<v-row v-if="dialogError">
							<v-col cols="12">
								<p>Napaka, javi Alešu kodo S1</p>
								<p v-html="dialogError" class="red--text"></p>
							</v-col>
						</v-row>
					</v-container>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="blue darken-1" text @click="dialog = false">Prekini</v-btn>
					<v-btn color="blue darken-1" text @click="saveForm">Shrani</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { saveAs } from 'file-saver';
import fields from './Skode/fields';

export default {
	created() {
		// this.refreshRecords();
	},
	mounted() {
		this.allHeaders = fields;
		this.visibleFields = this.allHeaders.filter((el) => el.visible).map((el) => el.value);
		this.itemsPerPage = parseInt(localStorage.getItem('skode-itemsPerPage')) || 15;
		this.fromYear = parseInt(localStorage.getItem('skode-fromYear')) || 2023;
		this.toYear = parseInt(localStorage.getItem('skode-toYear')) || 2023;
		this.toggleStatus = JSON.parse(localStorage.getItem('skode-toggleStatus')) || ['P'];
		this.search = localStorage.getItem('skode-search') || '';
		this.groupBy = localStorage.getItem('skode-groupBy') || '+';
		// console.log("mounted skode");
		this.refreshRecords();
	},
	data: () => ({
		dialog: false,
		form: {
			name: '',
			group_code: 'skode.search',
		},
		dialogError: null,
		loading: false,
		search: '',
		allHeaders: [],
		visibleFields: [],
		headers: [],
		records: [],
		size: 99999,
		itemsPerPage: 15,
		itemsPerPageOptions: globalItemsPerPageOptions,
		toggleStatus: ['P'],
		// groupBy: "Pogodba_ID",
		groupBy: '+',
		groupByOptions: [
			{ value: '+', text: 'Brez grupiranja' },
			{ value: '+ Datum_SQL', text: 'Po datumu' },
			{ value: '- Datum_SQL', text: 'Po datumu padajoče' },
			{ value: '+ Pogodba_ID', text: 'Po pogodbi' },
			{ value: '- Pogodba_ID', text: 'Po pogodbi padajoče' },
			{ value: '+ Agent_ID', text: 'Po agentu' },
			{ value: '- Agent_ID', text: 'Po agentu padajoče' },
			{ value: '+ Delavnica', text: 'Po delavnici' },
			{ value: '- Delavnica', text: 'Po delavnici padajoče' },
			{ value: '+ Stranka', text: 'Po stranki' },
			{ value: '- Stranka', text: 'Po stranki padajoče' },
			{ value: '+ Leto', text: 'Po letu' },
			{ value: '- Leto', text: 'Po letu padajoče' },
		],
		fromYear: 2024,
		fromYearOptions: [2026, 2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012, 2011, 2010],
		toYear: 2026,
		toYearOptions: [2026, 2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012, 2011, 2010],
	}),
	watch: {
		toggleStatus: (val) => {
			localStorage.setItem('skode-toggleStatus', JSON.stringify(val));
		},
		fromYear: (val) => {
			localStorage.setItem('skode-fromYear', val);
		},
		toYear: (val) => {
			localStorage.setItem('skode-toYear', val);
		},
		itemsPerPage: (val) => {
			localStorage.setItem('skode-itemsPerPage', val);
		},
		groupBy: (val) => {
			localStorage.setItem('skode-groupBy', val);
		},
		search: (val) => {
			localStorage.setItem('skode-search', val);
		},
		visibleFields: {
			handler(val) {
				this.headers = this.allHeaders.filter((item) => this.visibleFields.indexOf(item.value) > -1);
			},
		},
	},
	computed: {
		fieldsOptions() {
			return fields;
		},
		filteredRecords() {
			const ret = this.records.filter((item) => (item.Leto >= this.fromYear) & (item.Leto <= this.toYear) & this.toggleStatus.includes(item.Status));

			console.log(ret[0]);

			return ret;
		},
		realGroupBy() {
			var ret;
			if (this.groupBy === '+') {
				ret = null;
			} else {
				let [_sort, _field] = this.groupBy.split(' ');
				ret = _field;
			}
			return ret;
		},
		realGroupSortDesc() {
			var ret;
			if (this.groupBy === '+') {
				ret = false;
			} else {
				let [_sort, _field] = this.groupBy.split(' ');
				ret = _sort === '+' ? false : true;
			}
			return ret;
		},
	},
	methods: {
		filterByManyFields(value, search, item) {
			// console.log("filter by many");
			let isReady = value != null && search != null && typeof value === 'string';
			if (!isReady) return false;
			// console.log("filter by many - go in");

			// console.log("item:", item);

			var ret = true;
			let splitSearch = search.split(',');

			splitSearch.forEach((element) => {
				var oneWordRet = false;

				let searchStringLC = item.search_string.toLowerCase();
				let el = element.trim().toLowerCase();
				let ID = parseInt(item.Pogodba_ID);
				// console.log(element, ID, searchStringLC);

				oneWordRet = oneWordRet || searchStringLC.indexOf(el) !== -1;
				oneWordRet = oneWordRet || ID == parseInt(element);

				ret = ret && oneWordRet;
			});

			// console.log(ret);

			return ret;
		},

		handleClick(record) {
			this.$router.push(`/skode/${record.ID}/uredi`);
		},

		showSaveModal() {
			this.dialog = true;
		},

		async reload() {
			this.loading = true;

			let that = this;
			this.$axios.get('/clear').then((response) => {
				that.$axios.get('/v_skode_full').then((response_2) => {
					that.records = eval(response_2.data);
					that.recordCount = that.records.length;
					that.loading = false;
				});
				this.loading = false;
			});
		},

		async exportInternalDataToXLS() {
			let arr = this.$refs.table._data.internalCurrentItems;
			let ret = [];

			let header = [];
			this.visibleFields.forEach((el) => header.push(el));

			arr.forEach((element) => {
				let items = [];
				Object.keys(element).forEach((key, index) => {
					if (this.visibleFields.indexOf(key) > -1) {
						items.push(element[key]);
					}
				});
				ret.push(items);
			});

			// console.log(ret);

			let wb = new ExcelJS.Workbook();
			wb.calcProperties.fullCalcOnLoad = true;
			const ws = wb.addWorksheet('Škode - izvoz');
			ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];
			const row = ws.addRow(header);
			row.font = { bold: true };

			ret.forEach((el) => {
				ws.addRow(el);
			});

			const buf = await wb.xlsx.writeBuffer();
			saveAs(new Blob([buf]), 'skode-izvoz-' + this.search + '.xlsx');
		},

		setSearchValue() {
			this.search = this.$refs.searchInput.$el.getElementsByTagName('input')[0].value;
		},
		refreshRecords() {
			this.loading = true;
			this.$axios.get('/v_skode_full').then((response) => {
				this.records = eval(response.data);
				this.recordCount = this.records.length;
				this.loading = false;
			});
		},

		saveForm() {
			const that = this;
			var saveFormFields = Object.assign({}, that.form);
			saveFormFields.name = this.$refs.formName.$el.getElementsByTagName('input')[0].value;
			// not using v-model as vuetify datatable interferes

			this.$axios
				.post(`/skode/reports`, saveFormFields)
				.then(function (response) {
					let el = that.$refs.formName.$el.getElementsByTagName('input')[0];
					el._value = '';
					el.value = '';

					that.dialog = false;
				})
				.catch(function (error) {
					that.dialogError = error.message;
				});
		},
	},
};
</script>

<style lang="scss">
.mr-36 {
	margin-right: 9rem;
}

.width-from-year {
	max-width: 5rem;
}
.width-fields {
	max-width: 12rem;
}
.width-group-by {
	max-width: 12rem;
}
.search {
	max-width: 20rem;
}
.status-P {
	background-color: lightyellow;
	display: flex;
	height: 100%;
	align-items: center;
	justify-content: flex-end;
	margin-left: 6px;
	padding-right: 6px;
}
.status-O {
	background-color: lightgreen;
	display: flex;
	height: 100%;
	align-items: center;
	justify-content: flex-end;
	margin-left: 6px;
	padding-right: 6px;
}
.status-Z {
	background-color: red;
	color: white;
	display: flex;
	height: 100%;
	align-items: center;
	justify-content: flex-end;
	margin-left: 6px;
	padding-right: 6px;
}

.header-grid {
	display: grid;
	grid-template-columns: 1fr;
	text-align: center;
	width: 100%;
	column-gap: 6px;
	row-gap: 12px;
}

@media #{map-get($display-breakpoints, 'lg-and-up')} {
	.header-grid {
		grid-template-columns: 1fr auto auto auto auto auto auto;
		row-gap: 0px;
	}
}

td {
	cursor: pointer !important;
}

.v-menu__content {
	// hack to get more items into v-select
	max-height: 60% !important;
}

.v-data-table--dense td {
	padding: 0 4px !important;
}

tbody tr:nth-of-type(odd) {
	background-color: rgba(255, 0, 0, 0.05);
}

.v-application .mr-24 {
	margin-right: 96px !important;
}

@media #{map-get($display-breakpoints, 'xs-only')} {
	.v-data-table .v-data-table__mobile-row {
		justify-content: left;
		min-height: 0px;
		padding-top: 3px;
		padding-bottom: 3px;
	}

	.v-application--is-ltr .v-data-table__mobile-row__cell {
		padding-left: 6px;
	}

	.v-data-footer {
		flex-wrap: nowrap;
	}
	.v-application--is-ltr .v-data-footer__select .v-select {
		margin: 13px 0 13px 6px;
	}
}
</style>
